/**
 * Navigation behaviour behind hashtags and @mentions.
 *
 * Both renderers use these hooks — `MentionText` for plain post/comment text
 * and `HtmlContent` for rich-text bodies — so a hashtag behaves identically
 * whichever path rendered it.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeed } from '@wlb/shared/context/FeedContext';
import { useAuthGate } from '../feedback/AuthGate';
import { useOpenUserProfile } from '../../features/profile/useOpenUserProfile';
import { formatHashtag } from '../../utils/hashtags';
import type { MentionMatch } from '../../utils/mentions';

/**
 * Opens the feed filtered by a hashtag. The tag also lands in the URL so the
 * filter survives a refresh and the back button.
 */
export const useHashtagNavigate = (): ((tag: string) => void) => {
  const navigate = useNavigate();
  const { searchHashtag } = useFeed();
  const { requireAuth } = useAuthGate();

  return useCallback(
    (tag: string) => {
      const label = formatHashtag(tag);
      if (!label) return;
      requireAuth(() => {
        navigate(`/?tag=${encodeURIComponent(label.slice(1))}`);
        // Matching is case-insensitive on both sides; the tag travels in the
        // canonical `#tag` form.
        searchHashtag(label).catch(() => undefined);
      });
    },
    [navigate, searchHashtag, requireAuth],
  );
};

/**
 * Opens the profile behind an @mention.
 *
 * The caller passes the identity resolved when the text was parsed, so
 * navigation never re-reads the visible label: a renamed user still resolves,
 * and a label shared by two members (`ambiguous`) toasts rather than opening
 * the wrong profile.
 */
export const useMentionNavigate = (): ((match: MentionMatch) => void) => {
  const openUserProfile = useOpenUserProfile();
  const { requireAuth } = useAuthGate();

  return useCallback(
    (match: MentionMatch) => {
      requireAuth(() => openUserProfile(match.ambiguous ? null : match.userGuid));
    },
    [openUserProfile, requireAuth],
  );
};
